import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Slide,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import PayrollViewController from "../../redux/actions/payrollApiAction";
import { BRAND, primaryButtonSx, mobileInputSx, inputSx } from "../../components/auth/authTheme";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SheetHandle = () => (
  <Box sx={{ display: "flex", justifyContent: "center", pt: 1.25, pb: 0.5 }}>
    <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: BRAND.border }} />
  </Box>
);

const AddSalaryReportMonth = ({ show, handleClose }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const { genrateSalaryReport } = PayrollViewController();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) {
      setStartDate(null);
      setEndDate(null);
      setError("");
    }
  }, [show]);

  const today = dayjs().endOf("day");

  const validateRange = (start, end) => {
    if (!start || !end) return "";
    if (start.isAfter(today, "day") || end.isAfter(today, "day")) {
      return "Dates must be today or in the past";
    }
    if (end.isBefore(start, "day")) {
      return "End date cannot be before start date";
    }
    return "";
  };

  const handleStartChange = (value) => {
    setStartDate(value);
    if (value && endDate && endDate.isBefore(value, "day")) {
      setEndDate(value);
    }
    setError(validateRange(value, endDate && endDate.isBefore(value, "day") ? value : endDate));
  };

  const handleEndChange = (value) => {
    setEndDate(value);
    setError(validateRange(startDate, value));
  };

  const handleGenerate = () => {
    const validationError = validateRange(startDate, endDate);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!startDate || !endDate) return;

    const payload = {
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD"),
    };
    dispatch(genrateSalaryReport(payload));
    handleClose();
  };

  const canGenerate = Boolean(startDate && endDate && !validateRange(startDate, endDate));

  const datePickerSlotProps = {
    textField: {
      fullWidth: true,
      sx: fieldSx,
    },
    mobilePaper: {
      sx: { borderRadius: "16px 16px 0 0" },
    },
    dialog: {
      sx: {
        "& .MuiDialog-container": { alignItems: "flex-end" },
      },
    },
  };

  const content = (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? 1.5 : 2 }}>
        <DatePicker
          label="Start Date"
          value={startDate}
          onChange={handleStartChange}
          maxDate={today}
          disableFuture
          defaultCalendarMonth={today}
          slotProps={datePickerSlotProps}
        />
        <DatePicker
          label="End Date"
          value={endDate}
          onChange={handleEndChange}
          minDate={startDate || undefined}
          maxDate={today}
          disableFuture
          defaultCalendarMonth={startDate || today}
          slotProps={datePickerSlotProps}
        />

        {startDate && endDate && !error && (
          <Box
            sx={{
              py: 1,
              px: 1.5,
              borderRadius: "10px",
              bgcolor: BRAND.orangeLight,
              border: `1px solid ${BRAND.orangeHover}33`,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted }}>
              Report period
            </Typography>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 700, color: BRAND.navy }}>
              {startDate.format("DD MMM YYYY")} – {endDate.format("DD MMM YYYY")}
            </Typography>
          </Box>
        )}

        {error && (
          <Typography sx={{ fontSize: "0.8125rem", color: "#d32f2f", px: 0.5 }}>
            {error}
          </Typography>
        )}

        <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted, lineHeight: 1.5 }}>
          Select a date range up to today. Salary reports include payroll generated within
          this period.
        </Typography>
      </Box>
    </LocalizationProvider>
  );

  const actions = (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: isMobile ? "stretch" : "flex-end",
        gap: 1,
        width: "100%",
        px: 2,
        pb: 2,
        pt: 0.5,
        boxSizing: "border-box",
      }}
    >
      <Button
        onClick={handleGenerate}
        variant="contained"
        disabled={!canGenerate}
        fullWidth={isMobile}
        sx={{
          ...primaryButtonSx,
          ...(isMobile && { minHeight: 48, borderRadius: "12px", order: isMobile ? 0 : 1 }),
        }}
      >
        Generate
      </Button>
      <Button
        onClick={handleClose}
        variant="outlined"
        color="inherit"
        fullWidth={isMobile}
        sx={{
          ...(isMobile && {
            minHeight: 44,
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
          }),
        }}
      >
        Cancel
      </Button>
    </Box>
  );

  if (isMobile) {
    return (
      <Dialog
        open={show}
        onClose={handleClose}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="xs"
        sx={{ "& .MuiDialog-container": { alignItems: "flex-end" } }}
        PaperProps={{
          sx: {
            m: 0,
            width: "100%",
            maxWidth: "100%",
            borderRadius: "16px 16px 0 0",
            pb: "max(env(safe-area-inset-bottom), 8px)",
          },
        }}
      >
        <SheetHandle />
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography sx={{ fontWeight: 700, color: BRAND.navy, fontSize: "1.125rem" }}>
            Generate Salary Report
          </Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted, mt: 0.25 }}>
            Choose a past date range for the report
          </Typography>
        </Box>
        <DialogContent sx={{ px: 2, pt: 0.5, pb: 1 }}>{content}</DialogContent>
        <Box sx={{ width: "100%", borderTop: `1px solid ${BRAND.border}` }}>{actions}</Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <Box sx={{ px: 3, pt: 2.5, pb: 0.5 }}>
        <Typography sx={{ fontWeight: 700, color: BRAND.navy, fontSize: "1.125rem" }}>
          Generate Salary Report
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted, mt: 0.25 }}>
          Choose a past date range for the report
        </Typography>
      </Box>
      <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>{content}</DialogContent>
      <Box sx={{ borderTop: `1px solid ${BRAND.border}`, px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={!canGenerate}
          sx={primaryButtonSx}
        >
          Generate
        </Button>
      </Box>
    </Dialog>
  );
};

export default AddSalaryReportMonth;
